import { zodResolver } from '@hookform/resolvers/zod';
import {
  Badge,
  Button,
  Card,
  Cell,
  Column,
  EmptyState,
  FieldGrid,
  Row,
  TableFrame,
  TableHeader,
} from '@skupreme/shared/ui';
import {
  FormNumberField,
  FormSelect,
  FormTextField,
} from '@skupreme/shared/ui/form';
import { CommonUtility } from '@utility/common';
import StorageProviderService from '@utility/services/storage-provider';
import { ToastMessage } from '@utility/toast';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
  type CarrierRate,
  carryOunces,
  EMPTY_PARCEL,
  pricedRates,
  type RateCalculatorValues,
  rateCalculatorSchema,
  SPEED_OPTIONS,
  toRatePayload,
} from '../../lib/rate-calculator';

const RATE_COLUMNS = 3;

function Rates({
  rates,
  isStale,
  isLoading,
}: {
  rates: CarrierRate[] | null;
  isStale: boolean;
  isLoading: boolean;
}) {
  if (!rates && !isLoading) {
    return (
      <Card title="Rates">
        <EmptyState
          title="No rates yet"
          description="Enter the destination and the package, then get rates."
        />
      </Card>
    );
  }

  const priced = pricedRates(rates ?? []);
  return (
    <div className="sk:flex sk:flex-col sk:gap-2">
      <TableFrame
        aria-label="Rates"
        columnCount={RATE_COLUMNS}
        isLoading={isLoading}
        loadingRows={3}
        emptyMessage="No carrier quoted this package"
        className={isStale ? 'sk:opacity-50' : undefined}
        header={
          <TableHeader>
            <Column id="carrier" isRowHeader>
              Carrier
            </Column>
            <Column id="service">Service</Column>
            <Column id="rate" className="sk:w-40">
              Rate
            </Column>
          </TableHeader>
        }
      >
        {priced.map((rate, index) => (
          <Row
            key={`${rate.provider.key}-${rate.service.key}`}
            id={`${rate.provider.key}-${rate.service.key}`}
          >
            <Cell>{rate.provider.key}</Cell>
            <Cell>{rate.service.key}</Cell>
            <Cell>
              <span className="sk:inline-flex sk:items-center sk:gap-2 sk:tabular-nums">
                {CommonUtility.currencyFormat(rate.rate, rate.currency)}
                {index === 0 && priced.length > 1 ? (
                  <Badge tone="success">Cheapest</Badge>
                ) : null}
              </span>
            </Cell>
          </Row>
        ))}
      </TableFrame>
      {isStale && !isLoading ? (
        <span className="sk:text-xs sk:text-fg-tertiary">
          The package changed since these rates. Get rates again.
        </span>
      ) : null}
    </div>
  );
}

/** Prices a parcel from this provider to a zip, per carrier and service. */
export function RateCalculatorTab({
  providerId,
  providerName,
}: {
  providerId: string;
  providerName?: string;
}) {
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState<CarrierRate[] | null>(null);
  const [quoted, setQuoted] = useState<string | null>(null);

  const { control, handleSubmit, setValue, trigger } =
    useForm<RateCalculatorValues>({
      resolver: zodResolver(rateCalculatorSchema),
      defaultValues: { ...EMPTY_PARCEL, shipFrom: providerName ?? '' },
    });
  const values = useWatch({ control });
  const current = JSON.stringify(values);

  useEffect(() => {
    setValue('shipFrom', providerName ?? '');
  }, [providerName, setValue]);

  /* Typing 16 oz or more rolls the extra over into pounds. */
  useEffect(() => {
    const carried = carryOunces(values.pounds ?? null, values.ounces ?? null);
    if (carried) {
      setValue('pounds', carried.pounds);
      setValue('ounces', carried.ounces);
      void trigger('pounds');
    }
  }, [values.pounds, values.ounces, setValue, trigger]);

  const calculate = handleSubmit(async form => {
    setLoading(true);
    try {
      const { data } = await StorageProviderService.providerRate(
        providerId,
        toRatePayload(form),
      );
      setRates(data ?? []);
      setQuoted(current);
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setLoading(false);
    }
  });

  return (
    <div className="sk:grid sk:grid-cols-2 sk:items-start sk:gap-4">
      <Card title="Calculate rates">
        <form onSubmit={calculate} className="sk:flex sk:flex-col sk:gap-4">
          <FieldGrid columns={3}>
            <FormTextField
              control={control}
              name="shipFrom"
              label="Ship from"
              isDisabled
            />
            <FormTextField
              control={control}
              name="shipTo"
              label="Ship to zip"
              isRequired
              isDisabled={loading}
            />
            <FormSelect
              control={control}
              name="speed"
              label="Speed"
              options={SPEED_OPTIONS}
              isDisabled={loading}
            />
          </FieldGrid>
          <FieldGrid columns={3}>
            <FormNumberField
              control={control}
              name="pounds"
              label="Weight (lbs)"
              isDisabled={loading}
            />
            <FormNumberField
              control={control}
              name="ounces"
              label="Weight (oz)"
              isDisabled={loading}
            />
            <span />
            <FormNumberField
              control={control}
              name="length"
              label="Length (in.)"
              isRequired
              isDisabled={loading}
            />
            <FormNumberField
              control={control}
              name="width"
              label="Width (in.)"
              isRequired
              isDisabled={loading}
            />
            <FormNumberField
              control={control}
              name="height"
              label="Height (in.)"
              isRequired
              isDisabled={loading}
            />
          </FieldGrid>
          <div className="sk:flex sk:justify-end">
            <Button type="submit" variant="primary" isPending={loading}>
              Get rates
            </Button>
          </div>
        </form>
      </Card>
      <Rates
        rates={rates}
        isLoading={loading}
        isStale={!!rates && quoted !== current}
      />
    </div>
  );
}
